import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import FlightStageTag from '@features/regional-overview/digital-traps/components/flight-stage-tag/flight-stage-tag';
import type { FlightRealizationEnum } from '@features/regional-overview/digital-traps/domain/flight-stage.enum';
import {
  FLIGHT_CURVE_GRAPH_FLIGHT_STAGE_EMPTY,
  FLIGHT_CURVE_GRAPH_FLIGHT_STAGE_LOADING
} from '@features/regional-overview/digital-traps/domain/tests-id.constants';
import type { FlightStagePredictions } from 'querys/regional-overview/regional-overview.query.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlightStageColumn,
  FlightStageContainer,
  FlightStageEmpty,
  FlightStageGrid,
  FlightStageLoading,
  FlightStageLoadingContainer
} from './graph-flight-stage.styles';
import { generateDateKeysForFilter, mapPredictionsToColumns } from './graph-flight-stage.utils';

interface GraphFlightStageProps {
  selectedDays: number;
  predictions: FlightStagePredictions[] | null | undefined;
  isLoading: boolean;
  hasLegend: boolean;
}

export const GraphFlightStage = ({ selectedDays, predictions, isLoading, hasLegend }: GraphFlightStageProps) => {
  const [t] = useTranslation();
  const dateKeysForGraph = useMemo(() => generateDateKeysForFilter(selectedDays), [selectedDays]);

  const totalColumns = useMemo(() => selectedDays + 6, [selectedDays]);

  const predictionColumns = useMemo(() => mapPredictionsToColumns(predictions, dateKeysForGraph), [predictions, dateKeysForGraph]);

  if (isLoading) {
    return (
      <FlightStageLoadingContainer $hasLegend={hasLegend}>
        <FlightStageGrid $totalColumns={totalColumns}>
          <FlightStageLoading data-testid={FLIGHT_CURVE_GRAPH_FLIGHT_STAGE_LOADING}>
            <Spin indicator={<LoadingOutlined spin />} size='small' />
            {t('pages.regional_overview.digital_traps.flight_curve.loading_flight_prediction')}
          </FlightStageLoading>
        </FlightStageGrid>
      </FlightStageLoadingContainer>
    );
  }

  if (!predictions?.length) {
    return (
      <FlightStageLoadingContainer $hasLegend={hasLegend}>
        <FlightStageGrid $totalColumns={totalColumns}>
          <FlightStageEmpty data-testid={FLIGHT_CURVE_GRAPH_FLIGHT_STAGE_EMPTY}>
            <InfoCircleOutlined />
            {t('pages.regional_overview.digital_traps.flight_curve.empty_flight_prediction')}
          </FlightStageEmpty>
        </FlightStageGrid>
      </FlightStageLoadingContainer>
    );
  }

  return (
    <FlightStageContainer $hasLegend={hasLegend}>
      <FlightStageGrid $totalColumns={totalColumns}>
        {predictionColumns.map(column => (
          <FlightStageColumn
            key={column.prediction.start_date}
            $gridColumnStart={column.gridColumnStart}
            $gridColumnEnd={column.gridColumnEnd}>
            <FlightStageTag
              flightGeneration={column.prediction.generation}
              flightStage={column.prediction.stage as FlightRealizationEnum}
            />
          </FlightStageColumn>
        ))}
      </FlightStageGrid>
    </FlightStageContainer>
  );
};
